import { useEffect, useRef } from 'react'
import { createConsumer, Cable } from '@rails/actioncable'
import type { ChannelEvent } from '@/types/conversations'

interface ProjectChannelOptions {
  projectId: number
  onEvent: (event: ChannelEvent) => void
  onConnected?: () => void
  onDisconnected?: () => void
}

/**
 * Hook to subscribe to ProjectChannel via ActionCable
 * Receives project-level events (deploy status, workflow progress, domain/SEO updates, etc.)
 */
export function useProjectChannel({
  projectId,
  onEvent,
  onConnected,
  onDisconnected
}: ProjectChannelOptions) {
  const consumerRef = useRef<Cable | null>(null)
  const channelRef = useRef<any>(null)

  // Store latest callbacks in refs to avoid recreating subscription
  const onEventRef = useRef(onEvent)
  const onConnectedRef = useRef(onConnected)
  const onDisconnectedRef = useRef(onDisconnected)

  // Update refs when callbacks change
  useEffect(() => {
    onEventRef.current = onEvent
    onConnectedRef.current = onConnected
    onDisconnectedRef.current = onDisconnected
  })

  useEffect(() => {
    consumerRef.current = createConsumer()

    channelRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'ProjectChannel',
        project_id: projectId
      },
      {
        received: (data: ChannelEvent) => {
          console.log('📡 Project event received:', data.type, data)
          onEventRef.current(data)
        },
        connected: () => {
          console.log('✅ Connected to ProjectChannel', projectId)
          onConnectedRef.current?.()
        },
        disconnected: () => {
          console.log('❌ Disconnected from ProjectChannel', projectId)
          onDisconnectedRef.current?.()
        },
        rejected: () => {
          console.error('🚫 ProjectChannel subscription rejected', projectId)
        }
      }
    )

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
      }
    }
  }, [projectId])

  return {
    channel: channelRef.current,
    consumer: consumerRef.current,
    isConnected: !!channelRef.current
  }
}
